import type { APIError } from '@vizit/api';
import { useShowTagging } from '@vizit/feature-flags';
import { AssetTypeSelect, ProductCategoryComboBox, TagFilter } from '@vizit/organisms';
import { useAssetTable } from '../../providers';
import { useProductCategoryList } from './asset-table-filters.hooks';

/**
 * Enum representing the available filter options for the asset table
 */
enum AssetTableFilterOptions {
  ImageType = 'imageType',
  ProductCategory = 'assetCategoryId',
  ProductCategoryName = 'assetCategoryName',
  Tags = 'tags',
}

interface AssetTableFiltersProps {
  /**
   * Organization ID to fetch categories for
   *
   * @optional
   * @type {string}
   */
  organizationId?: string;
  /**
   * Function to handle authentication error
   *
   * @type {(error: APIError) => void}
   * @param error - The authentication error
   */
  onAuthenticationError: (error: APIError) => void;
}

/**
 * Component that renders filter controls for the asset table.
 * Supports filtering by product category and image type.
 * Uses AssetTableProvider context for state management.
 *
 * @param props - Component props
 * @returns JSX element
 */
function AssetTableFilters(props: AssetTableFiltersProps) {
  const { organizationId, onAuthenticationError } = props;

  const {
    workspaceId,
    productCategoryId,
    productCategoryName,
    imageType,
    tagIds,
    onFilterProductCategory,
    onFilterImageType,
    onFilterTags,
  } = useAssetTable();
  const showTagging = useShowTagging();

  const {
    items: productCategoryItems,
    isLoading: isLoadingProductCategory,
    filterText: productCategoryFilterText,
    setFilterText: setProductCategoryFilterText,
    loadMore: loadMoreProductCategory,
    onSelectionChange: onCategorySelectionChange,
  } = useProductCategoryList({
    organizationId,
    workspaceId,
    filterText: productCategoryName || '',
    onAuthenticationError,
    onFilterProductCategory,
  });

  return (
    <div className="flex flex-row justify-between">
      <div className="flex flex-row gap-8 pb-8">
        <div className="min-w-[280px]">
          <ProductCategoryComboBox
            filterText={productCategoryFilterText}
            setFilterText={setProductCategoryFilterText}
            items={productCategoryItems}
            selectedKey={productCategoryId}
            onSelectionChange={onCategorySelectionChange}
            isLoading={isLoadingProductCategory}
            loadMore={loadMoreProductCategory}
            data-testid="library-assets-category-filter"
          />
        </div>
        <div className="min-w-[280px]">
          <AssetTypeSelect
            assetType={imageType}
            onAssetTypeChange={onFilterImageType}
            data-testid="library-assets-image-type-filter"
          />
        </div>
        {showTagging && workspaceId && (
          <TagFilter
            workspaceId={workspaceId}
            selectedTagIds={tagIds ?? []}
            onSelectedTagIdsChange={onFilterTags}
            className="min-w-[280px]"
            data-testid="library-assets-tag-filter"
          />
        )}
      </div>
    </div>
  );
}

export { AssetTableFilters, AssetTableFilterOptions };
